/**
 * API client do construtor de Formularios (modulo Comercial).
 *
 * Um Formulario eh uma coleta configuravel de campos que pode capturar Leads
 * (tipo CAPTURA/CAMPANHA — alimenta o CRM) ou apenas registrar respostas
 * (PESQUISA/OUTRO). Cada campo mapeia opcionalmente para um atributo do Lead
 * (`mapeia_para`) e pode ter logica condicional (mostrar/ocultar conforme a
 * resposta de outro campo).
 *
 * Endpoints internos (autenticados — recebem o `request` do useAuth):
 *   GET    /api/formularios                 lista compacta
 *   POST   /api/formularios                 cria {nome, tipo, descricao?}
 *   GET    /api/formularios/{id}            detalhe (+ campos)
 *   PATCH  /api/formularios/{id}            atualiza settings
 *   DELETE /api/formularios/{id}
 *   POST   /api/formularios/{id}/duplicar
 *   POST   /api/formularios/{id}/campos     {campos:[...]} upsert + reorder
 *   GET    /api/formularios/{id}/respostas   paginada + filtros (FORM-26)
 *   DELETE /api/formularios/{id}/respostas/{rid}              (FORM-27, MFA)
 *   POST   /api/formularios/{id}/respostas/{rid}/reprocessar  (FORM-13)
 *   GET    /api/formularios/{id}/metricas   mesmos filtros de /respostas
 *   GET    /api/formularios/pendencias      contadores do sino (FORM-13)
 *
 * Endpoints publicos (sem auth — fetch direto, NAO usam `request`):
 *   GET    /api/public/formularios/{slug}
 *   POST   /api/public/formularios/{slug}/responder
 *
 * Padrao do projeto: endpoints sem trailing slash.
 */

import { ACCESS_API_URL as API_BASE } from "../config/api";
import { CAMPOS_ENDERECO } from "../utils/endereco";

const PREFIXO = "/api/formularios";

// ============================================================
// Catalogo de tipos de campo (palette + render + labels)
// ============================================================

// `temOpcoes`     — abre o editor de lista de opcoes (SELECT/RADIO/...).
// `semInput`      — separador/titulo (SECAO): nao gera valor nem entra em
//                   validacao/condicao.
// `aceitaMapeia`  — pode mapear para um atributo do Lead (mapeia_para).
//                   Multi-valor (MULTI_SELECT/CHECKBOX) tambem mapeia: vira
//                   lista em `tags` e texto separado por virgula nos demais.
export const TIPOS_CAMPO = [
  { value: "TEXTO", label: "Texto curto", temOpcoes: false, semInput: false, aceitaMapeia: true },
  { value: "TEXTAREA", label: "Texto longo", temOpcoes: false, semInput: false, aceitaMapeia: true },
  { value: "EMAIL", label: "E-mail", temOpcoes: false, semInput: false, aceitaMapeia: true },
  { value: "TELEFONE", label: "Telefone", temOpcoes: false, semInput: false, aceitaMapeia: true },
  { value: "NUMERO", label: "Número", temOpcoes: false, semInput: false, aceitaMapeia: true },
  { value: "DATA", label: "Data", temOpcoes: false, semInput: false, aceitaMapeia: true },
  { value: "SELECT", label: "Lista suspensa", temOpcoes: true, semInput: false, aceitaMapeia: true },
  { value: "MULTI_SELECT", label: "Seleção multipla", temOpcoes: true, semInput: false, aceitaMapeia: true },
  { value: "RADIO", label: "Escolha única (radio)", temOpcoes: true, semInput: false, aceitaMapeia: true },
  { value: "CHECKBOX", label: "Caixas de seleção", temOpcoes: true, semInput: false, aceitaMapeia: true },
  { value: "CPF_CNPJ", label: "CPF / CNPJ", temOpcoes: false, semInput: false, aceitaMapeia: true },
  { value: "ARQUIVO", label: "Upload de arquivo", temOpcoes: false, semInput: false, aceitaMapeia: false },
  { value: "SECAO", label: "Seção (título)", temOpcoes: false, semInput: true, aceitaMapeia: false },
];

export const TIPO_CAMPO_LABEL = TIPOS_CAMPO.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

export const TIPO_CAMPO_MAP = TIPOS_CAMPO.reduce((acc, t) => {
  acc[t.value] = t;
  return acc;
}, {});

export function tipoTemOpcoes(tipo) {
  return Boolean(TIPO_CAMPO_MAP[tipo]?.temOpcoes);
}

export function tipoSemInput(tipo) {
  return Boolean(TIPO_CAMPO_MAP[tipo]?.semInput);
}

export function tipoAceitaMapeia(tipo) {
  return Boolean(TIPO_CAMPO_MAP[tipo]?.aceitaMapeia);
}

// ============================================================
// Tipos de formulario
// ============================================================

// Valores ESPELHAM exatamente o backend (formularios.models.Formulario.tipo):
// CAPTURA_LEAD / ENRIQUECER_LEAD / CAMPANHA / GENERICO.
// CAPTURA_LEAD e CAMPANHA criam Lead novo — por isso so esses dois expoem
// etapa_destino + responsavel_padrao + origem_lead nas settings.
// `tocaLead` = a resposta escreve num lead (cria OU enriquece) — e o que
// decide quem exibe as `tags_padrao` (FORM-30). GENERICO nao toca no CRM.
export const TIPOS_FORMULARIO = [
  { value: "CAPTURA_LEAD", label: "Captura de leads", geraLead: true, tocaLead: true },
  { value: "ENRIQUECER_LEAD", label: "Enriquecer lead existente", geraLead: false, tocaLead: true },
  { value: "CAMPANHA", label: "Campanha / landing", geraLead: true, tocaLead: true },
  { value: "GENERICO", label: "Generico / pesquisa", geraLead: false, tocaLead: false },
];

export const TIPO_FORMULARIO_LABEL = TIPOS_FORMULARIO.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

export function tipoGeraLead(tipo) {
  return Boolean(TIPOS_FORMULARIO.find((t) => t.value === tipo)?.geraLead);
}

/** O lead criado ou enriquecido recebe as `tags_padrao` do formulario? */
export function tipoUsaTagsPadrao(tipo) {
  return Boolean(TIPOS_FORMULARIO.find((t) => t.value === tipo)?.tocaLead);
}

// Default de `origem_lead` no backend (FormularioCriarSerializer). O Editor
// nao oferece mais "— Selecionar —": ele mandava origem vazia e TODO lead do
// site entrava como "Outro", zerando o Site no relatorio de origem (FORM-29).
export const ORIGEM_LEAD_PADRAO = "SITE";

// Limites de `tags_padrao` (formularios.services.MAX_TAGS_PADRAO / TAMANHO).
export const MAX_TAGS_PADRAO = 10;
export const MAX_TAMANHO_TAG = 40;

// ============================================================
// Mapeamento campo -> atributo do Lead
// ============================================================

// "" = nao mapeia (resposta fica so no payload da submissao). Os demais
// espelham atributos do Lead/Contato no backend.
export const MAPEIA_PARA_OPCOES = [
  { value: "", label: "Não mapear (campo livre)" },
  // Contato — os rotulos sao os mesmos do card do lead no CRM.
  { value: "contato_nome", label: "Nome completo", grupo: "Contato" },
  { value: "contato_sobrenome", label: "Apelido (como prefere ser chamado)", grupo: "Contato" },
  { value: "contato_email", label: "E-mail", grupo: "Contato" },
  { value: "contato_telefone", label: "Telefone / WhatsApp", grupo: "Contato" },
  { value: "telefone_secundario", label: "Telefone 2", grupo: "Contato" },
  { value: "email_secundario", label: "E-mail 2", grupo: "Contato" },
  // CRM-15/FORM-48: o CPF/CNPJ vai ao lead e segue ao cliente na conversao.
  { value: "documento", label: "CPF / CNPJ do contato", grupo: "Contato" },
  // Perfil
  { value: "empresa", label: "Empresa", grupo: "Perfil" },
  { value: "cargo", label: "Cargo", grupo: "Perfil" },
  { value: "instagram", label: "Instagram", grupo: "Perfil" },
  { value: "site", label: "Site", grupo: "Perfil" },
  { value: "data_nascimento", label: "Nascimento (data)", grupo: "Perfil" },
  { value: "descricao_bio", label: "Bio / contexto", grupo: "Perfil" },
  // Endereco (ADR-0071) — todos gravam no endereco PRINCIPAL do lead, que o
  // backend cria com o titulo "Principal" quando o lead ainda nao tem
  // endereco. Mesma ordem de `CAMPOS_ENDERECO`: CEP primeiro, porque ele
  // preenche logradouro, bairro, cidade e UF do proprio formulario.
  { value: "cep", label: "CEP", grupo: "Endereço" },
  { value: "logradouro", label: "Logradouro (rua)", grupo: "Endereço" },
  { value: "numero", label: "Número", grupo: "Endereço" },
  { value: "complemento", label: "Complemento", grupo: "Endereço" },
  { value: "bairro", label: "Bairro", grupo: "Endereço" },
  { value: "cidade", label: "Cidade", grupo: "Endereço" },
  { value: "estado", label: "UF (sigla ou nome do estado)", grupo: "Endereço" },
  // Negocio
  { value: "titulo", label: "Descrição do negócio (título do lead)", grupo: "Negócio" },
  { value: "valor_estimado", label: "Valor estimado", grupo: "Negócio" },
  { value: "origem", label: "Origem (como nos conheceu)", grupo: "Negócio" },
  { value: "temperatura", label: "Temperatura (Quente / Morno / Frio)", grupo: "Negócio" },
  { value: "status", label: "Status do lead", grupo: "Negócio" },
  { value: "tags", label: "Tags (acrescenta às tags do lead)", grupo: "Negócio" },
  { value: "proxima_acao", label: "Próxima ação", grupo: "Negócio" },
  { value: "data_proxima_acao", label: "Data da próxima ação", grupo: "Negócio" },
  { value: "observacoes", label: "Observações", grupo: "Negócio" },
];

// Grupos na ordem de exibicao, para o <optgroup> do editor de campo.
export const MAPEIA_PARA_GRUPOS = MAPEIA_PARA_OPCOES.reduce((acc, o) => {
  if (!o.grupo) return acc;
  let g = acc.find((x) => x.grupo === o.grupo);
  if (!g) {
    g = { grupo: o.grupo, opcoes: [] };
    acc.push(g);
  }
  g.opcoes.push(o);
  return acc;
}, []);

export const MAPEIA_PARA_LABEL = MAPEIA_PARA_OPCOES.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

// Os UNICOS destinos que aceitam mais de um campo apontando para eles: no
// servidor as respostas viram linhas "Rotulo: valor" (FORM-23). Nos demais, o
// ultimo campo vencia e o outro sumia sem aviso.
export const DESTINOS_DE_TEXTO_LIVRE = ["observacoes", "descricao_bio"];

export function destinoAceitaVariosCampos(destino) {
  return DESTINOS_DE_TEXTO_LIVRE.includes(destino);
}

// Dinheiro: uma faixa ("Ate R$ 20 mil") nunca vira numero, entao o destino de
// valor so vale para texto e numero (FORM-03).
export const DESTINO_VALOR_ESTIMADO = "valor_estimado";
export const TIPOS_COM_VALOR_ESTIMADO = ["TEXTO", "NUMERO"];

// ============================================================
// Logica condicional (branching)
// ============================================================

export const ACOES_CONDICAO = [
  { value: "MOSTRAR", label: "Mostrar este campo" },
  { value: "OCULTAR", label: "Ocultar este campo" },
];

export const MODOS_CONDICAO = [
  { value: "TODAS", label: "todas as regras" },
  { value: "QUALQUER", label: "qualquer regra" },
];

// `precisaValor: false` para operadores que testam presenca, nao igualdade —
// nesses o campo de valor fica oculto no builder.
export const OPERADORES_CONDICAO = [
  { value: "IGUAL", label: "e igual a", precisaValor: true },
  { value: "DIFERENTE", label: "e diferente de", precisaValor: true },
  { value: "CONTEM", label: "contem", precisaValor: true },
  { value: "PREENCHIDO", label: "esta preenchido", precisaValor: false },
  { value: "VAZIO", label: "esta vazio", precisaValor: false },
];

export const OPERADOR_CONDICAO_LABEL = OPERADORES_CONDICAO.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

export function operadorPrecisaValor(operador) {
  const op = OPERADORES_CONDICAO.find((o) => o.value === operador);
  // Default true: operadores desconhecidos pedem valor (fail-safe).
  return op ? op.precisaValor : true;
}

// ============================================================
// Helpers de URL publica
// ============================================================

/** URL absoluta da pagina publica de resposta a partir do slug.
 *
 * `caminho` (FORM-12): o backend devolve `/f/{slug}?convite={token}` ao gerar
 * o convite de enriquecimento — passa-se ele inteiro no lugar do slug.
 */
export function urlPublicaFormulario(slug, { caminho } = {}) {
  const path = caminho || `/f/${slug}`;
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

/** ENRIQUECER_LEAD nao tem link divulgavel: o convite sai do cockpit do lead. */
export function tipoExigeConvite(tipo) {
  return tipo === "ENRIQUECER_LEAD";
}
export const MSG_LINK_PELO_COCKPIT = "Envie pelo cockpit do lead (Enviar formulário).";

// ============================================================
// Campo ARQUIVO (FORM-05) — regua alinhada ao backend (formularios/models.py)
// ============================================================

export const EXTENSOES_ARQUIVO_FORMULARIO = ["pdf", "jpg", "jpeg", "png", "heic", "dwg"];
export const MAX_BYTES_ARQUIVO_FORMULARIO = 10 * 1024 * 1024;
export const MAX_ARQUIVOS_POR_CAMPO = 3;
export const ACCEPT_ARQUIVO_FORMULARIO = EXTENSOES_ARQUIVO_FORMULARIO.map((e) => `.${e}`).join(",");
const LISTA_EXTENSOES = EXTENSOES_ARQUIVO_FORMULARIO.map((e) => e.toUpperCase()).join(", ");
export const DICA_ARQUIVO_FORMULARIO =
  `Até ${MAX_ARQUIVOS_POR_CAMPO} arquivos de 10 MB cada: ${LISTA_EXTENSOES}.`;

/** Mensagem de erro para um File escolhido no campo ARQUIVO, ou null. */
export function erroDoArquivoDoFormulario(arquivo) {
  if (!arquivo) return "Escolha um arquivo.";
  const nome = String(arquivo.name || "").toLowerCase();
  const ext = nome.includes(".") ? nome.split(".").pop() : "";
  if (!EXTENSOES_ARQUIVO_FORMULARIO.includes(ext)) {
    return `Tipo não aceito. Envie ${LISTA_EXTENSOES}.`;
  }
  if (arquivo.size > MAX_BYTES_ARQUIVO_FORMULARIO) return "Arquivo excede 10 MB.";
  return null;
}

/** Valor do campo ARQUIVO gravado na resposta: `[{nome, anexo_id}]` (ja anexado)
 *  ou tokens (integracao ainda FALHOU). Devolve os anexos reais. */
export function anexosDoValorDeArquivo(valor) {
  if (!Array.isArray(valor)) return [];
  return valor.filter((v) => v && typeof v === "object" && v.anexo_id);
}

// ============================================================
// Ingestao a partir de site externo (ADR-0063)
// ============================================================

/** Endpoint que a landing page externa chama. */
export function urlIngestaoLead(slug) {
  return `${API_BASE}/api/public/leads/${slug}`;
}

/**
 * Nome de campo que a landing usa, por campo do formulario.
 *
 * E o `mapeia_para` — vocabulario estavel, ao contrario do UUID, que muda
 * quando o gestor recria o campo no builder. Campo sem mapeamento nao e
 * enderecavel de fora (a tela Publicar avisa isso).
 *
 * O schema publico (`/api/public/formularios/{slug}`) omite `mapeia_para` e
 * expoe o mesmo codigo como `nome_externo` — por isso os dois sao lidos: a
 * pagina /f/slug e o builder enxergam o mesmo destino.
 */
export function nomeExternoDoCampo(campo) {
  return campo?.mapeia_para || campo?.nome_externo || "";
}

/** O destino grava no endereco principal do lead (ADR-0071)? */
export function destinoEhEndereco(destino) {
  return CAMPOS_ENDERECO.includes(destino);
}

const NL = "\n";

/** `<form>` HTML puro, pronto para colar. Nao passa por CORS. */
function escaparHtml(texto) {
  return String(texto ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Controle HTML que corresponde ao tipo do campo.
 *
 * Nao e' capricho visual: o backend valida por tipo. SELECT com valor
 * fora das opcoes, CHECKBOX que nao chega como lista e DATA fora de
 * AAAA-MM-DD sao REJEITADOS. Um `<input type="text">` para todos, como
 * era antes, gerava um formulario que respondia 400 no site do cliente
 * — e o gestor nao tinha como descobrir por que.
 */
/** Chaves de campanha que os trechos copiam da URL da landing (FORM-33). */
export const CHAVES_UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/** Chaves de instrucao que o servidor entende no corpo (nao sao campos). */
export const CAMPO_HONEYPOT = "_gotcha";
export const CAMPO_TEMPO_MS = "_tempo_ms";
export const CAMPO_PAGE_URL = "page_url";
export const CAMPO_REDIRECT_ERRO = "_redirect_erro";
/** Query string com que o form nativo volta ao site em caso de erro (FORM-34). */
export const PARAMETRO_ERRO = "pharu_erro";

function ehGrupoDeCaixas(campo) {
  return campo.tipo === "MULTI_SELECT" || campo.tipo === "CHECKBOX";
}

function controleDoCampo(campo, nome) {
  const req = campo.obrigatorio ? " required" : "";
  const opcoes = campo.opcoes || [];

  switch (campo.tipo) {
    case "SELECT":
    case "RADIO":
      return [
        `    <select name="${nome}"${req}>`,
        '      <option value="">Selecione...</option>',
        ...opcoes.map(
          (o) => `      <option value="${escaparHtml(o)}">${escaparHtml(o)}</option>`,
        ),
        "    </select>",
      ].join(NL);

    case "MULTI_SELECT":
    case "CHECKBOX":
      // Mesma `name` repetida: e' assim que o HTML manda varios valores.
      // O grupo sai em <fieldset>, nao em <label> aninhado: label dentro
      // de label e' invalido, e o clique no texto da opcao acabaria
      // ativando o controle do label de fora.
      // `data-obrigatorio` e' o que o script do trecho le: o HTML nao tem
      // `required` para "pelo menos uma caixa", e sem isso o navegador
      // deixava enviar vazio e o servidor recusava (FORM-34).
      return [
        `    <fieldset${campo.obrigatorio ? ` data-obrigatorio="${escaparHtml(campo.label)}"` : ""}>`,
        ...opcoes.map(
          (o) =>
            `      <label><input type="checkbox" name="${nome}" value="${escaparHtml(o)}" /> ${escaparHtml(o)}</label>`,
        ),
        `    </fieldset>`,
      ].join(NL);

    case "TEXTAREA":
      return `    <textarea name="${nome}"${req}></textarea>`;

    case "DATA":
      // type=date faz o navegador mandar AAAA-MM-DD, que e' o que o
      // backend exige — sem ele o visitante digita 22/08/2026 e leva 400.
      return `    <input type="date" name="${nome}"${req} />`;

    case "NUMERO":
      return `    <input type="number" name="${nome}"${req} />`;

    case "EMAIL":
      return `    <input type="email" name="${nome}"${req} />`;

    case "TELEFONE":
      return `    <input type="tel" name="${nome}"${req} />`;

    default:
      return `    <input type="text" name="${nome}"${req} />`;
  }
}

/**
 * Script que acompanha o `<form>` HTML (FORM-33, FORM-34, FORM-49).
 *
 * Preenche os campos escondidos com a campanha da URL da landing
 * (`utm_*`, `page_url`), guarda a propria pagina como destino de erro,
 * mede o tempo de preenchimento no relogio do visitante e barra o envio
 * de um grupo de caixas obrigatorio vazio — que o HTML sozinho nao barra.
 */
function scriptDoFormularioHtml(idForm) {
  return [
    "<script>",
    "(function () {",
    `  var form = document.getElementById("${idForm}");`,
    "  if (!form) return;",
    "  var abertoEm = Date.now();",
    "  var parametros = new URLSearchParams(location.search);",
    `  ${JSON.stringify(CHAVES_UTM)}.forEach(function (chave) {`,
    "    var valor = parametros.get(chave);",
    "    if (valor && form.elements[chave]) form.elements[chave].value = valor;",
    "  });",
    `  form.elements["${CAMPO_PAGE_URL}"].value = location.href;`,
    `  form.elements["${CAMPO_REDIRECT_ERRO}"].value = location.href;`,
    '  form.addEventListener("submit", function (evento) {',
    `    form.elements["${CAMPO_TEMPO_MS}"].value = String(Date.now() - abertoEm);`,
    '    var grupos = form.querySelectorAll("fieldset[data-obrigatorio]");',
    "    for (var i = 0; i < grupos.length; i += 1) {",
    '      if (!grupos[i].querySelector("input:checked")) {',
    "        evento.preventDefault();",
    '        alert("Marque pelo menos uma opção em: " + grupos[i].getAttribute("data-obrigatorio"));',
    "        return;",
    "      }",
    "    }",
    "    // Duplo clique numa API lenta enviava duas vezes e criava dois leads.",
    '    var botao = form.querySelector("button[type=submit]");',
    "    if (botao) botao.disabled = true;",
    "  });",
    "})();",
    "</script>",
  ].join(NL);
}

export function snippetFormularioHtml(slug, campos, { redirect = "" } = {}) {
  const idForm = `pharu-form-${slug}`;
  const enderecaveis = (campos || []).filter((c) => nomeExternoDoCampo(c));
  const inputs = enderecaveis
    .map((c) => {
      const nome = nomeExternoDoCampo(c);
      // Grupo de caixas nao tem um controle unico para o <label>
      // apontar; ele usa <fieldset>/<legend>, que e' o par certo.
      if (ehGrupoDeCaixas(c)) {
        return controleDoCampo(c, nome).replace(
          /^ {4}<fieldset([^>]*)>/,
          `    <fieldset$1>
      <legend>${escaparHtml(c.label)}</legend>`,
        );
      }
      return [
        `  <label>${escaparHtml(c.label)}`,
        controleDoCampo(c, nome),
        "  </label>",
      ].join(NL);
    })
    .join(NL);

  const linhas = [
    `<form id="${idForm}" action="${urlIngestaoLead(slug)}" method="POST">`,
    inputs,
    "  <!-- Anti-robo: mantenha escondido e vazio. Bot preenche, humano nao ve. -->",
    `  <input type="text" name="${CAMPO_HONEYPOT}" style="display:none" tabindex="-1" autocomplete="off" />`,
    "  <!-- Campanha e pagina de origem: o script abaixo preenche a partir da URL. -->",
    ...CHAVES_UTM.map((chave) => `  <input type="hidden" name="${chave}" value="" />`),
    `  <input type="hidden" name="${CAMPO_PAGE_URL}" value="" />`,
    `  <input type="hidden" name="${CAMPO_TEMPO_MS}" value="" />`,
    "  <!-- Em caso de erro o visitante volta para esta pagina com ?pharu_erro=... (so se o dominio estiver cadastrado). -->",
    `  <input type="hidden" name="${CAMPO_REDIRECT_ERRO}" value="" />`,
  ];
  if (redirect) {
    linhas.push(`  <input type="hidden" name="_redirect" value="${escaparHtml(redirect)}" />`);
  }
  linhas.push('  <button type="submit">Enviar</button>', "</form>", scriptDoFormularioHtml(idForm));
  return linhas.filter((linha) => linha !== null && linha !== undefined).join(NL);
}


/** Envio por `fetch` (exige o dominio cadastrado em origens permitidas). */
export function snippetFetch(slug, campos) {
  const enderecaveis = (campos || []).filter((c) => nomeExternoDoCampo(c));
  const corpo = enderecaveis
    .map((c) => {
      const nome = nomeExternoDoCampo(c);
      // Grupo de caixas manda a mesma chave varias vezes: getAll pega
      // todas; fromEntries/get ficaria so com a ultima marcada.
      const multi = c.tipo === "MULTI_SELECT" || c.tipo === "CHECKBOX";
      return `      ${nome}: ${multi ? `dados.getAll("${nome}")` : `dados.get("${nome}")`},`;
    })
    .join(NL);

  // O trecho vem como um listener de submit inteiro, e nao como um
  // `fetch` solto, por causa do tempo de preenchimento: ele precisa
  // marcar quando a PAGINA abriu, nao quando o envio acontece. Num
  // `fetch` solto, quem copiasse mandaria 0 ms e o servidor descartaria
  // o envio como robo — respondendo 201, sem criar lead. O trecho tem de
  // sair daqui pronto para colar, nao pronto para dar errado em silencio.
  // A duracao vai em `_tempo_ms`, medida no relogio do proprio visitante:
  // comparar o relogio dele com o do servidor descartava humano com o
  // relogio adiantado (FORM-49). A campanha vem da URL da landing
  // (FORM-33), nunca de um valor fixo.
  return [
    "// Marque quando a página abriu — fora de qualquer função, para",
    "// rodar no carregamento. Envio concluído em menos de 2s é robô.",
    "const abertoEm = Date.now();",
    "",
    "const formulario = document.querySelector(\"#seu-formulario\");",
    "// Anti-robô: um campo escondido que só robô preenche. Coloque dentro do seu <form>:",
    `// <input type="text" name="${CAMPO_HONEYPOT}" style="display:none" tabindex="-1" autocomplete="off" />`,
    "",
    'formulario.addEventListener("submit", async (evento) => {',
    "  evento.preventDefault();",
    "  // Duplo clique numa API lenta enviava duas vezes e criava dois leads.",
    '  const botao = formulario.querySelector("button[type=submit]");',
    "  if (botao) botao.disabled = true;",
    "  const dados = new FormData(formulario);",
    "  // Campanha (utm_*) da URL desta página, para saber de onde veio o lead.",
    "  const utm = {};",
    "  new URLSearchParams(location.search).forEach((valor, chave) => {",
    '    if (chave.startsWith("utm_")) utm[chave] = valor;',
    "  });",
    "",
    `  const resposta = await fetch("${urlIngestaoLead(slug)}", {`,
    '    method: "POST",',
    '    headers: { "Content-Type": "application/json" },',
    "    body: JSON.stringify({",
    corpo,
    "      ...utm,",
    `      ${CAMPO_PAGE_URL}: location.href,`,
    `      ${CAMPO_HONEYPOT}: dados.get("${CAMPO_HONEYPOT}") || "",`,
    `      ${CAMPO_TEMPO_MS}: Date.now() - abertoEm,`,
    "    }),",
    "  });",
    "",
    "  if (botao) botao.disabled = false;",
    "  if (resposta.ok) {",
    '    formulario.reset();',
    '    alert("Recebemos seu contato.");',
    "  } else {",
    "    const { detalhe } = await resposta.json().catch(() => ({}));",
    '    alert(detalhe || "Não foi possível enviar agora.");',
    "  }",
    "});",
  ].filter((linha) => linha !== null && linha !== undefined).join(NL);
}



// ============================================================
// Endpoints internos (autenticados)
// ============================================================

export function listarFormularios(request) {
  return request(PREFIXO);
}

export function obterFormulario(request, id) {
  return request(`${PREFIXO}/${id}`);
}

/**
 * Etapas e vendedores que o Editor pode oferecer: `{etapas:[{id,nome,tipo}],
 * usuarios:[{id,nome}]}` — ativos, da unidade, sem a coluna de conversao.
 *
 * Exige so `formulario:manage`. O Editor lia `/api/crm/pipeline` (`lead:view`)
 * e `/api/usuarios` (`usuario:view`): o cargo Marketing levava 403 silencioso,
 * o select caia no PIPELINE_PADRAO com ids "seed-contato" e o Salvar devolvia
 * "Deve ser um UUID válido" (FORM-28). Sem fallback: em erro, a tela avisa e
 * desabilita os selects.
 */
export function obterOpcoesFormulario(request, opcoes = {}) {
  return request(`${PREFIXO}/opcoes`, opcoes);
}

/**
 * Quantos formularios dependem de cada coluna do pipeline:
 * `{etapas: {[etapaId]: {total, formularios:[nome]}}}` (ate 5 nomes).
 *
 * Exige `pipeline:configurar`. E o aviso que faltava em Configurar pipeline:
 * removida a coluna, os formularios que apontavam para ela eram remapeados em
 * silencio (FORM-01).
 */
export function listarEtapasEmUso(request, opcoes = {}) {
  return request(`${PREFIXO}/etapas-em-uso`, opcoes);
}

export function criarFormulario(request, { nome, tipo, descricao }) {
  const body = { nome, tipo };
  if (descricao) body.descricao = descricao;
  return request(PREFIXO, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Atualiza settings do formulario (nome, descricao, tipo, ativo, slug,
 * etapa_destino, responsavel_padrao, origem_lead, tags_padrao, config{...}).
 * Body parcial — envie so o que mudou.
 */
/**
 * Dispara uma ingestao de teste PELA API AUTENTICADA.
 *
 * O botao de teste nao pode bater no endereco publico direto: o ERP e'
 * outra origem e nao esta na allowlist do formulario do cliente — o
 * navegador bloqueava e a tela exibia "erro de rede", acusando de
 * quebrada uma integracao que funcionava. A action do backend executa o
 * mesmo tradutor e o mesmo service da rota publica.
 */
export function testarIngestaoFormulario(request, id, dados, opcoes = {}) {
  return request(`/api/formularios/${id}/testar-ingestao`, {
    method: "POST",
    body: JSON.stringify({ dados }),
    ...opcoes,
  });
}

/** `opcoes` vai ao `request` (ex.: `{ silentError: true }` quando a tela ja avisa o erro). */
export function atualizarFormulario(request, id, campos, opcoes = {}) {
  return request(`${PREFIXO}/${id}`, {
    ...opcoes,
    method: "PATCH",
    body: JSON.stringify(campos),
  });
}

export function excluirFormulario(request, id) {
  return request(`${PREFIXO}/${id}`, { method: "DELETE" });
}

export function duplicarFormulario(request, id) {
  return request(`${PREFIXO}/${id}/duplicar`, { method: "POST" });
}

/**
 * Salva o conjunto de campos de uma vez (upsert + reorder). O backend
 * cria os campos sem `id`, atualiza os que tem `id`, remove os ausentes e
 * reordena pela posicao no array (campo `ordem`). Retorna o formulario
 * detalhe atualizado.
 */
export function salvarCampos(request, id, campos) {
  return request(`${PREFIXO}/${id}/campos`, {
    method: "POST",
    body: JSON.stringify({ campos }),
  });
}

// ============================================================
// Respostas (FORM-13 / FORM-24 / FORM-26 / FORM-27)
// ============================================================

/** `status_integracao` da resposta: o efeito no CRM aconteceu ou falhou. */
export const INTEGRACAO_OK = "OK";
export const INTEGRACAO_FALHOU = "FALHOU";
export const STATUS_INTEGRACAO_LABEL = {
  [INTEGRACAO_OK]: "Integrada",
  [INTEGRACAO_FALHOU]: "Falhou no CRM",
};

/** `efeito_lead`: lead criado, enriquecido, vinculado ou nenhum (FORM-24). */
export const EFEITO_LEAD_LABEL = {
  CRIADO: "Lead criado",
  ENRIQUECIDO: "Lead enriquecido",
  // FORM-15: contato de lead aberto da unidade — a resposta vai para ele.
  VINCULADO: "Juntou ao lead aberto",
  NENHUM: "Nenhum",
};

/** Motivos contados em `metricas.recusas.por_motivo` (FORM-13). */
export const MOTIVO_RECUSA_LABEL = {
  VALIDACAO: "Validação de campo",
  CHAVE_DESCONHECIDA: "Chave desconhecida",
  ANTISPAM: "Descartado pelo anti-spam",
  INATIVO: "Formulário inativo",
  TAMANHO: "Envio grande demais",
  TETO: "Teto do formulário atingido",
};

/** Teto que o backend aceita em `por_pagina` (core.pagination). */
export const RESPOSTAS_POR_PAGINA_MAXIMO = 200;
export const RESPOSTAS_POR_PAGINA_PADRAO = 20;

/** Filtros aceitos por /respostas e /metricas; vazio nao vai na query. */
const FILTROS_DE_RESPOSTA = [
  "origem", "com_lead", "status_integracao", "de", "ate", "busca", "ordering",
];

/**
 * Query string dos filtros da tela de Respostas (FORM-26). So entram as
 * chaves conhecidas e com valor; `pagina`/`por_pagina` vao a parte.
 */
export function queryDeRespostas(filtros = {}, { pagina, porPagina } = {}) {
  const params = new URLSearchParams();
  FILTROS_DE_RESPOSTA.forEach((chave) => {
    const valor = filtros[chave];
    if (valor === undefined || valor === null || valor === "") return;
    params.set(chave, String(valor));
  });
  if (pagina) params.set("pagina", String(pagina));
  if (porPagina) params.set("por_pagina", String(porPagina));
  const texto = params.toString();
  return texto ? `?${texto}` : "";
}

/**
 * Respostas paginadas e filtradas:
 * {resultados: [...], total, pagina, por_pagina, ordering}.
 * Cada resposta: {id, formulario, lead, lead_numero, lead_nome, dados,
 * campos_snapshot, origem_submissao, status_integracao, erro_integracao,
 * efeito_lead, eh_teste, anonimizada_em, ip?, user_agent? (so com
 * formulario:manage), utm, criado_por, criado_por_nome, created_at}.
 */
export function listarRespostas(request, id, filtros = {}, paginacao = {}) {
  return request(`${PREFIXO}/${id}/respostas${queryDeRespostas(filtros, paginacao)}`);
}

/**
 * Todas as respostas do filtro, pagina a pagina (para exportar). Para no
 * total informado pelo servidor.
 */
export async function listarTodasRespostas(request, id, filtros = {}) {
  const todas = [];
  let pagina = 1;
  let total = Infinity;
  while (todas.length < total) {
    // A exportacao e' sequencial por natureza: a pagina N+1 depende de saber
    // o total devolvido na pagina N.
    // eslint-disable-next-line no-await-in-loop
    const lote = await listarRespostas(request, id, filtros, {
      pagina, porPagina: RESPOSTAS_POR_PAGINA_MAXIMO,
    });
    const itens = Array.isArray(lote?.resultados) ? lote.resultados : [];
    total = Number(lote?.total) || 0;
    todas.push(...itens);
    if (itens.length === 0) break;
    pagina += 1;
  }
  return todas;
}

/** DELETE da resposta (formulario:manage + MFA recente). Resolve com 204. */
export function excluirResposta(request, id, respostaId) {
  return request(`${PREFIXO}/${id}/respostas/${respostaId}`, { method: "DELETE" });
}

/**
 * Refaz o efeito no CRM de uma resposta FALHOU (idempotente). Devolve a
 * resposta atualizada; 400 {detalhe} quando o CRM recusa de novo.
 */
export function reprocessarResposta(request, id, respostaId, opcoes = {}) {
  return request(`${PREFIXO}/${id}/respostas/${respostaId}/reprocessar`, {
    method: "POST",
    ...opcoes,
  });
}

/**
 * Contadores do sino para formulario:manage:
 * {respostas_falhas, formularios_com_recusa, count}.
 */
export function contarPendenciasFormularios(request, opcoes = {}) {
  return request(`${PREFIXO}/pendencias`, opcoes);
}

/**
 * Metricas agregadas de um formulario, com os mesmos filtros de /respostas:
 * {total_respostas, por_origem[], respostas_teste, respostas_falhas,
 *  respostas_consideradas, leads_criados, leads_enriquecidos, rotulo_leads,
 *  percentual_leads, primeira_em, ultima_em, por_dia[],
 *  por_campo[{campo_id, label, tipo, respondido, exibido, taxa, distribuicao?}],
 *  campos_removidos[{campo_id, label, tipo, total_respondido}],
 *  recusas{hoje, ultimas_24h, total_30d, por_motivo[], ultimo_motivo,
 *  ultimo_detalhe, ultima_em}}.
 */
export function obterMetricasFormulario(request, id, filtros = {}) {
  return request(`${PREFIXO}/${id}/metricas${queryDeRespostas(filtros)}`);
}

// ============================================================
// Endpoints publicos (SEM auth — fetch direto)
// ============================================================

/**
 * fetch sem token para as rotas publicas (/api/public/...). Erro vira
 * `Error` com `status` (0 = sem conexao) e `body`. Tambem usado pela busca
 * de CEP da pagina publica (`services/enderecos.js`).
 */
export async function publicFetch(path, init = {}) {
  const { multipart, ...opcoes } = init;
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...opcoes,
      // Multipart: o navegador define o Content-Type com o boundary.
      headers: multipart
        ? { ...(opcoes.headers || {}) }
        : { "Content-Type": "application/json", ...(opcoes.headers || {}) },
    });
  } catch (_) {
    // Falha de rede vira "Failed to fetch" cru no banner do visitante.
    const erro = new Error("Sem conexão. Verifique a internet e tente de novo.");
    erro.status = 0;
    throw erro;
  }
  let body = null;
  try {
    body = await res.json();
  } catch (_) {
    /* sem body */
  }
  if (!res.ok) {
    // O handler de excecoes do backend reescreve 401/403/429 como {erro}.
    const mensagem =
      body?.detalhe ||
      body?.detail ||
      body?.erro ||
      (res.status === 429
        ? "Muitas tentativas. Aguarde alguns instantes e tente de novo."
        : `HTTP ${res.status}`);
    const erro = new Error(mensagem);
    erro.status = res.status;
    erro.body = body;
    throw erro;
  }
  return body;
}

export function visualizarFormularioPublico(slug, { convite } = {}) {
  const query = convite ? `?convite=${encodeURIComponent(convite)}` : "";
  return publicFetch(`/api/public/formularios/${slug}${query}`);
}

/**
 * Envia as respostas. `dados` eh {campo_id: valor}. `utm` e `lead` sao
 * opcionais (repassados da query string da pagina publica). `antispam`
 * ({gotcha, tempoMs}) vai como `_gotcha` e `_tempo_ms` — o servidor
 * descarta robo respondendo sucesso (FORM-49). Retorna
 * {ok, resposta_id, mensagem_sucesso, redirect_url?}.
 */
export function responderFormularioPublico(slug, { dados, utm, convite, antispam }) {
  const body = { dados };
  if (utm && Object.keys(utm).length > 0) body.utm = utm;
  // FORM-16: ENRIQUECER aponta o lead pelo convite; `lead` deixou de existir.
  if (convite) body.convite = convite;
  if (antispam) {
    body[CAMPO_HONEYPOT] = antispam.gotcha || "";
    if (Number.isFinite(antispam.tempoMs)) body[CAMPO_TEMPO_MS] = antispam.tempoMs;
  }
  return publicFetch(`/api/public/formularios/${slug}/responder`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Upload de um arquivo do campo ARQUIVO (FORM-05): multipart, antes do envio.
 * Resolve `{token, nome, tamanho_bytes}`; o token vai no valor do campo.
 */
export function enviarArquivoFormularioPublico(slug, arquivo, { convite } = {}) {
  const query = convite ? `?convite=${encodeURIComponent(convite)}` : "";
  const body = new FormData();
  body.append("arquivo", arquivo);
  return publicFetch(`/api/public/formularios/${slug}/arquivos${query}`, {
    method: "POST",
    body,
    multipart: true,
  });
}

// ============================================================
// Formulario visto do lead (cockpit) — FORM-12 / FORM-18
// ============================================================

/** {formularios: [{id, nome, slug}], lead_encerrado} — exige lead:edit. */
export function listarFormulariosDeEnriquecimento(request, leadId, opcoes = {}) {
  return request(`/api/crm/leads/${leadId}/convite-formulario`, opcoes);
}

/** 201 {convite_id, token, caminho, expira_em, formulario:{id, nome, slug}}.
 *  O token sai SO aqui (padrao C2): nenhuma listagem o devolve. */
export function gerarConviteFormulario(request, leadId, formularioId, opcoes = {}) {
  return request(`/api/crm/leads/${leadId}/convite-formulario`, {
    method: "POST",
    body: JSON.stringify({ formulario_id: formularioId }),
    ...opcoes,
  });
}

/** {respostas: [{id, formulario_id, formulario_nome, formulario_tipo, efeito_lead,
 *  status_integracao, origem_submissao, created_at, utm, campos:[{campo_id, label,
 *  tipo, valor}], anexos:[{id, nome_original, tamanho_bytes, download_url}]}]} */
export function listarRespostasDoLead(request, leadId, opcoes = {}) {
  return request(`/api/crm/leads/${leadId}/respostas-formulario`, opcoes);
}

/** POST /api/formularios/{id}/responder-interno — formulario:responder e, no
 *  backend, lead:edit (ENRIQUECER) ou lead:create (captacao). `lead` aponta o
 *  alvo do ENRIQUECER. Devolve a RespostaFormulario. */
export function responderFormularioInterno(request, id, { dados, lead, utm } = {}, opcoes = {}) {
  const body = { dados: dados || {} };
  if (lead) body.lead = lead;
  if (utm && Object.keys(utm).length > 0) body.utm = utm;
  return request(`/api/formularios/${id}/responder-interno`, {
    method: "POST",
    body: JSON.stringify(body),
    ...opcoes,
  });
}
